import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessagesSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Contact from '../components/Contact';
import FaqSection from '../components/trust/FaqSection';

type Step = { title: string; description: string };

const ContactPage = () => {
  const { t } = useTranslation('editor');
  const rawSteps = t('contactPage.steps', { returnObjects: true });
  const steps: Step[] = Array.isArray(rawSteps) ? (rawSteps as Step[]) : [];

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="fixed top-0 left-0 right-0 z-50">
        <Navbar />
      </div>

      <section className="relative pt-40 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-60 mask-fade-b" />
        <div className="pointer-events-none absolute top-16 left-1/2 -translate-x-1/2 w-[900px] h-[380px] rounded-full bg-gradient-to-r from-blue-600/25 via-cyan-500/20 to-emerald-500/15 blur-[120px] animate-aurora" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm font-medium mb-8 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            {t('contactPage.back')}
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-400/25 bg-cyan-400/[0.06] text-cyan-200 mb-6">
              <MessagesSquare className="h-3.5 w-3.5" />
              <span className="text-sm font-semibold tracking-wide">{t('contactPage.badge')}</span>
            </div>
            <h1 className="font-display text-5xl lg:text-6xl font-bold text-white mb-5">{t('contactPage.title')}</h1>
            <p className="text-xl text-slate-400 leading-relaxed">{t('contactPage.subtitle')}</p>
          </motion.div>

          <div className="mt-14">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-[0.2em] mb-5">{t('contactPage.stepsTitle')}</h2>
            <ol className="grid sm:grid-cols-3 gap-4">
              {steps.map((s, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + i * 0.08 }}
                  className="glass-card rounded-2xl p-6"
                >
                  <span className="font-display text-sm font-bold text-gradient">0{i + 1}</span>
                  <h3 className="text-lg font-semibold text-white mt-2 mb-1">{s.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{s.description}</p>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <Contact />
      <FaqSection />
      <Footer />
    </div>
  );
};

export default ContactPage;
